/**
 * **买入 / 卖出（申购 / 赎回 / 存入 / 取出）对话框** —— 批 `99` B2
 *
 * 依据：`投资域PRD` v2 §5（品种 × 动作矩阵）· §6.1（买入移动加权）· §6.2（卖出拆两腿）·
 * §6.4（手续费独立成笔）· `AC-INV1`/`AC-INV11` · `DR-059` D2/D3/D6 ·
 * 铁律 31（金额带币种）· 63（可选键剔键）· 8（红涨绿跌）。
 *
 * ## 🔴 三条端侧纪律
 *
 * 1. **一套底层，六种叫法**：`kind` 只决定按钮与标签文案（申购/赎回、存入/取出），
 *    落库永远是 `(quantityE4, priceE4, grossAmountCents)` + `action`（PRD §5 末段）。
 *    本组件不为品种分支写第二套逻辑。
 * 2. 🔴 **三个框（净值 / 份额 / 总价）各按自己的精度填，联动走锁定栈**（`DR-072` D6）：
 *    编辑一个 ⇒ 重算"最近编辑之外"的那个。派生一律调内核，端侧不自己除。
 * 3. **盈亏只预览、不入账**：真实 `realized_pnl` 与拆腿由服务端算（`DR-059` D3）。
 *    这里调内核 `sellBreakdown` / `ratioE4`（同一实现）只为让用户在提交前看清，不写库。
 *
 * ## ⚠️ 格式化一律走内核
 *
 * 端侧禁止 `/`、`toFixed`、`Math.round`（ESLint 块 A）⇒ 份额用 `e4ToTrimmedText`、
 * 收益率用 `ratioE4` + `percentWithSign`、金额用 `formatMoneyText`。**一处都不自己算**。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  HoldingKindValues,
  e4ToTrimmedText,
  formatMoneyText,
  ratioE4,
  sellBreakdown,
  toCents,
  type HoldingView,
  type PriceE4,
  type QtyE4,
} from '@jiaying/shared';
import { createTrade } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { invalidateAfterTxnWrite } from '../features/invalidate.js';
import { percentWithSign } from '../features/percent-text.js';
import {
  actionsOf,
  applyTradeEdit,
  buildTradeRequest,
  draftFromHolding,
  emptyTradeDraft,
  fillFullRedemption,
  hasMaturity,
  isAmountMode,
  isPriceDerived,
  kindLabelOf,
  previewTrade,
  qtyTextOf,
  validateTradeDraft,
  type TradeContext,
  type TradeDraft,
  type TradeField,
  type TradeMode,
} from '../features/investment.js';
import {
  BTN_CANCEL,
  BTN_FULL_REDEEM,
  DLG_TRADE_TITLE_BUY,
  F_ACCOUNT,
  F_CODE,
  F_DATE,
  F_FEE,
  F_GROSS,
  F_KIND,
  F_MATURITY,
  F_NAME,
  F_NET_PRICE,
  F_NOTE,
  F_QUANTITY,
  HINT_ACCOUNT,
  HINT_BUY,
  HINT_CLAMPED,
  HINT_FEE,
  HINT_GROSS,
  HINT_PRICE_DERIVED,
  HINT_SELL,
  HINT_TAIL_DIFF,
  HINT_TRADE_LINK,
  PREVIEW_ACTUAL,
  PREVIEW_COST,
  PREVIEW_PNL,
  PREVIEW_PROCEEDS,
} from '../features/investment-labels.js';
import { AccountPicker, type AccountPickerItemWithCurrency } from './AccountPicker.js';

export interface InvestmentTradeDialogProps {
  mode: TradeMode;
  /** 目标持仓；`null` = 新建买入 */
  holding: HoldingView | null;
  /**
   * 可选资金账户（页面已过滤为 `type='investment'`）。
   *
   * 🔴 用 `AccountPickerItemWithCurrency`（不是 `AccountPickerItem`）：本对话框要按
   * **币种**校验（卖出必须是持仓同币种的账户）⇒ 币种在这里是**必需**字段（批 `115` 的类型收窄）。
   */
  accounts: readonly AccountPickerItemWithCurrency[];
  /** 账本本地日历日 */
  today: string;
  onClose: () => void;
}

export function InvestmentTradeDialog({
  mode,
  holding,
  accounts,
  today,
  onClose,
}: InvestmentTradeDialogProps): ReactElement {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<TradeDraft>(() =>
    holding === null ? emptyTradeDraft(today) : draftFromHolding(holding, mode, today),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const patch = (p: Partial<TradeDraft>): void => {
    setDraft((d) => ({ ...d, ...p }));
    setSubmitError(null);
  };

  /**
   * 三框联动：**编辑一个 ⇒ 重算"最近编辑之外"的那个**（`DR-072` D6）。
   *
   * 🔴 必须走 `applyTradeEdit`（锁定栈在纯逻辑层，已单测）—— 在组件里再写一遍
   * "谁算谁"就是第二份实现（铁律 24）。
   */
  const patchField = (field: TradeField, value: string): void => {
    setDraft((d) => applyTradeEdit(d, field, value, holding));
    setSubmitError(null);
  };

  const ctx: TradeContext = { holding, accounts };
  const preview = previewTrade(draft);
  const amountMode = isAmountMode(draft.kind);
  /**
   * 初始焦点（`DR-072` D7）：理财 / 存款按金额录入，其余按份额录入。
   * 🔴 `isAmountMode` 现在**只**决定这件事 —— 三个框对所有品种都出现。
   */
  const focusField: TradeField = amountMode ? 'amount' : 'qty';
  const priceDerived = isPriceDerived(draft);
  const actions = actionsOf(draft.kind);
  const isNew = holding === null;
  const currency = holding?.currency ?? accounts.find((a) => a.id === draft.accountId)?.currency ?? 'CNY';

  /**
   * 卖出预览：成本与盈亏调内核 `sellBreakdown`（同一实现）。
   * 🔴 卖价取 `preview.priceE4`、回款取 `preview.amountCents`（成交金额是权威，`DR-072` D2）。
   */
  const sellPreview =
    mode === 'sell' && holding !== null && preview !== null
      ? sellBreakdown(
          preview.quantityE4 as QtyE4,
          holding.costPriceE4 as PriceE4,
          preview.priceE4 as PriceE4,
          preview.amountCents,
        )
      : null;

  const save = useMutation({
    mutationFn: () => createTrade(buildTradeRequest(draft)),
    onSuccess: () => {
      // 买卖会写资金腿 ⇒ 账户余额、净资产、流水都要失效（与记账同一批）
      invalidateAfterTxnWrite(qc);
      void qc.invalidateQueries({ queryKey: ['investment-overview'] });
      onClose();
    },
    onError: (e) => {
      setSubmitError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const submit = (): void => {
    const e = validateTradeDraft(draft, ctx);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    save.mutate();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {(mode === 'sell' ? actions.sell : isNew ? DLG_TRADE_TITLE_BUY : actions.buy) +
          (holding === null ? '' : ` · ${holding.name ?? holding.code ?? ''}`)}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* 品种：新建时要选；加仓 / 卖出取持仓（只读展示） */}
          {isNew ? (
            <TextField
              select
              size="small"
              required
              label={F_KIND}
              value={draft.kind}
              onChange={(e) => patch({ kind: e.target.value })}
              error={errors.kind !== undefined}
              helperText={errors.kind}
              inputProps={{ 'data-testid': 'trade-kind' }}
            >
              {HoldingKindValues.map((k) => (
                <MenuItem key={k} value={k}>
                  {kindLabelOf(k)}（{actionsOf(k).buy} / {actionsOf(k).sell}）
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="caption" color="text.secondary">
              品种：{kindLabelOf(draft.kind)} · 持仓 {qtyTextOf(holding)} 份
            </Typography>
          )}

          {isNew && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                required
                fullWidth
                label={F_NAME}
                value={draft.name}
                onChange={(e) => patch({ name: e.target.value })}
                error={errors.name !== undefined}
                helperText={errors.name}
                inputProps={{ 'data-testid': 'trade-name' }}
              />
              <TextField
                size="small"
                fullWidth
                label={F_CODE}
                value={draft.code}
                onChange={(e) => patch({ code: e.target.value })}
                inputProps={{ 'data-testid': 'trade-code' }}
              />
            </Stack>
          )}

          {/* 🔴 三框同屏：净值 / 份额 / 总价 —— 任填两项，第三项自动算，最后改的为准（`DR-072` D6） */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              required
              fullWidth
              inputMode="decimal"
              label={F_NET_PRICE}
              value={draft.priceText}
              onChange={(e) => patchField('price', e.target.value)}
              error={errors.priceText !== undefined}
              helperText={errors.priceText ?? (priceDerived ? HINT_PRICE_DERIVED : undefined)}
              inputProps={{ 'data-testid': 'trade-price' }}
            />
            <TextField
              size="small"
              required
              fullWidth
              inputMode="decimal"
              label={F_QUANTITY}
              value={draft.qtyText}
              onChange={(e) => patchField('qty', e.target.value)}
              error={errors.qtyText !== undefined}
              helperText={errors.qtyText}
              autoFocus={focusField === 'qty'}
              inputProps={{ 'data-testid': 'trade-qty' }}
            />
            <TextField
              size="small"
              required
              fullWidth
              inputMode="decimal"
              label={F_GROSS}
              value={draft.amountText}
              onChange={(e) => patchField('amount', e.target.value)}
              error={errors.amountText !== undefined}
              helperText={errors.amountText ?? HINT_GROSS}
              autoFocus={focusField === 'amount'}
              inputProps={{ 'data-testid': 'trade-amount' }}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {HINT_TRADE_LINK}
          </Typography>

          {/* 卖出：一键填入持仓精确份额（含历史遗留的 4 位尾数，否则永远清不了仓） */}
          {mode === 'sell' && holding !== null && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button
                size="small"
                onClick={() => patch(fillFullRedemption(draft, holding))}
                data-testid="trade-full-redeem"
              >
                {BTN_FULL_REDEEM}
              </Button>
              <Typography variant="caption" color="text.secondary">
                持仓 {qtyTextOf(holding)} 份
              </Typography>
            </Stack>
          )}

          {/* 反算夹持：份额被压到持仓上限时必须说清（否则用户以为系统吞了他的数） */}
          {draft.clamped && <Alert severity="info">{HINT_CLAMPED}</Alert>}

          {/* 尾差：成交金额 ≠ 份额 × 净值 ⇒ 明示"按你填的总价记账"（`AC-INV18`） */}
          {preview !== null && preview.differsFromInput && (
            <Alert severity="warning" data-testid="trade-preview">
              <Typography variant="body2">
                {PREVIEW_ACTUAL} {formatMoneyText(toCents(preview.amountCents), currency)}（
                {e4ToTrimmedText(preview.quantityE4)} 份）
              </Typography>
              <Typography variant="caption">{HINT_TAIL_DIFF}</Typography>
            </Alert>
          )}

          {/* 卖出：回款 / 成本 / 盈亏（调内核，同一实现） */}
          {sellPreview !== null && (
            <Box data-testid="trade-sell-preview">
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  {PREVIEW_PROCEEDS} {formatMoneyText(sellPreview.proceedsCents, currency)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {PREVIEW_COST} {formatMoneyText(sellPreview.costCents, currency)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {PREVIEW_PNL} {formatMoneyText(sellPreview.pnlCents, currency, { signed: true })}
                </Typography>
                {sellPreview.costCents > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    收益率 {percentWithSign(ratioE4(sellPreview.pnlCents, sellPreview.costCents))}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              fullWidth
              inputMode="decimal"
              label={F_FEE}
              value={draft.feeText}
              onChange={(e) => patch({ feeText: e.target.value })}
              error={errors.feeText !== undefined}
              helperText={errors.feeText ?? HINT_FEE}
              inputProps={{ 'data-testid': 'trade-fee' }}
            />
            <TextField
              size="small"
              required
              fullWidth
              type="date"
              label={F_DATE}
              value={draft.date}
              onChange={(e) => patch({ date: e.target.value })}
              error={errors.date !== undefined}
              helperText={errors.date}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'trade-date' }}
            />
          </Stack>

          <AccountPicker
            accounts={accounts}
            value={draft.accountId}
            onChange={(id) => patch({ accountId: id })}
            label={F_ACCOUNT}
            required
            error={errors.accountId !== undefined}
            helperText={errors.accountId ?? HINT_ACCOUNT}
          />

          {/* 到期日只对有到期概念的品种出现（理财/存款/债券） */}
          {hasMaturity(draft.kind) && (
            <TextField
              size="small"
              fullWidth
              type="date"
              label={F_MATURITY}
              value={draft.maturityDate}
              onChange={(e) => patch({ maturityDate: e.target.value })}
              error={errors.maturityDate !== undefined}
              helperText={errors.maturityDate}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'trade-maturity' }}
            />
          )}

          <TextField
            size="small"
            fullWidth
            label={F_NOTE}
            value={draft.note}
            onChange={(e) => patch({ note: e.target.value })}
            inputProps={{ 'data-testid': 'trade-note' }}
          />

          <Divider />
          <Typography variant="caption" color="text.secondary">
            {mode === 'sell' ? HINT_SELL : HINT_BUY}
          </Typography>

          {submitError !== null && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {BTN_CANCEL}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={save.isPending}
          data-testid="trade-submit"
        >
          {save.isPending ? '提交中…' : mode === 'sell' ? actions.sell : actions.buy}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
